// LISTA DE COMPRA (ADENDA z59, Diego 29-oct-2026, decisiones P1–P4): cuando se VENDE un proyecto, el cotizador congela con qué
// proveedor conviene comprar cada cosa y cuánto debería costar, para que Hiram (compras) compre en ese número o por debajo.
//  - P1: la META de cada partida = lo que cuesta con el MEJOR PROVEEDOR DEL MES confiable (condiciones por escrito y, si la marca lo
//        exige, distribuidor autorizado) al tipo de cambio del día de la venta (el FIX que puso Diego, sin colchón). Diego (y Carlos)
//        ven además el techo de costo que mantiene el verde.
//  - P2: Hiram y Nubia ven la meta, el proveedor por partida y el avance; NUNCA el precio de venta ni el margen.
//  - P3: se parte la compra entre proveedores solo si el ahorro paga el flete extra; sin dato de flete, no se parte.
//  - P4: la meta se CONGELA el día de la venta; si al bajarla la lista del mes cambió más de 3 %, se marca y decide Diego.
// Aquí viven las FORMAS (las comparten el motor, que la arma; el servidor, que la guarda y la sirve; y la pantalla de Diego) y el
// SOBRE de datos para el CRM de Carlos (bloque «costeo» del sobre v2, listo para cuando se prenda: hoy no se manda a ningún lado).
// La lista CONGELADA no viaja dentro de la cotización: se guarda aparte (config «venta.<id>») y solo la lee quien tiene `ver_costos`
// (Diego). Un vendedor que la pida por la API recibe 403.
import { z } from 'zod';
import { Fecha, FechaHora, Id, NivelRespaldo, OpcionId, Pesos, Texto } from './basicos.ts';
import { Semaforo } from './opciones.ts';
import type { ResultadoMotor } from './opciones.ts';
import { formatoPara } from './cuestionario.ts';
import type { Cuestionario } from './cuestionario.ts';
import { fechaHermosillo } from '../tiempo.ts';
import { DIAS_FIX_VIEJO } from '../constantes.ts';

const MESES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
/** «2026-10» → «octubre 2026» (la pantalla y la hoja dicen el mes en palabras, nunca en crudo). */
export function mesEnPalabras(am: string): string {
  const m = Number(am.slice(5, 7));
  return m >= 1 && m <= 12 ? `${MESES_ES[m - 1]} ${am.slice(0, 4)}` : am;
}
/** Un día (AAAA-MM-DD) o un instante (ISO: el día de Hermosillo, no el de UTC) en palabras: «29 de octubre de 2026». */
export function diaEnPalabras(iso: string): string {
  const dia = iso.length > 10 ? fechaHermosillo(new Date(iso)) : iso;
  const [a, m, d] = dia.split('-').map(Number);
  return `${d} de ${MESES_ES[(m ?? 1) - 1]} de ${a}`;
}

/** Categorías del Presupuesto de un proyecto en la plataforma de Carlos (COSTEO-PARA-HIRAM §2; los nombres exactos de
 *  budget_category_enum se confirman contra su `types.ts` antes de prender el CRM). */
export const CATEGORIAS_COMPRA = ['paneles', 'inversores', 'baterias', 'estructura', 'material_electrico', 'mano_obra', 'logistica', 'verificacion', 'otros', 'equipos_adicionales', 'trabajos_adicionales', 'viaticos'] as const;
export const CategoriaCompra = z.enum(CATEGORIAS_COMPRA);
export type CategoriaCompra = z.infer<typeof CategoriaCompra>;
export const ETIQUETA_CATEGORIA_COMPRA: Readonly<Record<CategoriaCompra, string>> = Object.freeze({
  paneles: 'Paneles',
  inversores: 'Inversores',
  baterias: 'Baterías',
  estructura: 'Estructura',
  material_electrico: 'Material eléctrico',
  mano_obra: 'Mano de obra',
  logistica: 'Envío',
  verificacion: 'UVIE / UIIE',
  otros: 'Trámites y otros',
  equipos_adicionales: 'Equipos adicionales',
  trabajos_adicionales: 'Trabajos adicionales',
  viaticos: 'Viáticos',
});

/** De dónde sale el costo de una partida. */
export const ORIGENES_PARTIDA = ['lista_mes', 'precio_diego', 'generico', 'tabla', 'capturado'] as const;
export const OrigenPartida = z.enum(ORIGENES_PARTIDA);
export type OrigenPartida = z.infer<typeof OrigenPartida>;

/** Marcas visibles en la hoja (P1–P4): lo que Hiram tiene que saber antes de comprar esa partida. */
export const MARCAS_PARTIDA = ['sin_proveedor_confiable', 'precio_diego', 'generico', 'sin_confirmar', 'lista_vieja', 'meta_mayor', 'flete_sin_dato', 'sin_lista'] as const;
export const MarcaPartida = z.enum(MARCAS_PARTIDA);
export type MarcaPartida = z.infer<typeof MarcaPartida>;
export const ETIQUETA_MARCA_PARTIDA: Readonly<Record<MarcaPartida, string>> = Object.freeze({
  sin_proveedor_confiable: 'Sin proveedor confiable',
  precio_diego: 'Precio de Diego',
  generico: 'Presupuesto genérico, sin proveedor',
  sin_confirmar: 'Costo sin confirmar',
  lista_vieja: 'Lista de hace más de 30 días',
  meta_mayor: 'Cuesta más que lo cotizado',
  flete_sin_dato: 'Flete sin dato',
  sin_lista: 'No viene en las listas del mes',
});

/** Cuánto puede cambiar la lista del mes contra la congelada antes de que lo decida Diego (P4). */
export const UMBRAL_CAMBIO_LISTA_PCT = 3;
/** Más días que esto entre el PRIMER día del mes de la lista y el día de la venta: «lista de hace más de 30 días» (verificación
 *  del 30-oct: contar desde el fin de mes dejaba pasar listas de casi 60 días). */
export const DIAS_LISTA_VIEJA = 30;
// Más días que DIAS_FIX_VIEJO (constantes.ts, 7: el mismo con que el motor avisa que el FIX de la tabla está viejo) entre el FIX de la
// meta y el día de la venta: la hoja lo dice en ROJO (el dólar se pudo mover; la meta en pesos también).
/** Tolerancia de la aserción (al peso): lo que suman las partidas contra el costo directo con el que se puso el precio. */
export const TOLERANCIA_CUADRE_MXN = 1;

const Dinero = z.number().finite();
const Usd = z.number().finite().nonnegative();

export const ComponenteCompra = z.strictObject({
  clave: Texto(1, 60),
  descripcion: Texto(1, 160),
  /** Por pieza de la partida (la batería con UN kit de cables y UN soporte). */
  cantidad: z.number().positive().max(100),
  precio_usd: Usd,
});
export type ComponenteCompra = z.infer<typeof ComponenteCompra>;

/** Una oferta de un proveedor para UNA pieza de la partida (el kit completo, con el monitoreo si el precio no lo trae). */
export const OfertaCompra = z.strictObject({
  proveedor: Texto(2, 60),
  unitario_usd: Usd,
  componentes: z.array(ComponenteCompra).min(1).max(8),
  mes_lista: z.string().regex(/^\d{4}-\d{2}$/),
  /** Por qué no es la recomendada («sin condiciones por escrito», «sin carta de distribuidor autorizado de Pylontech», «flete sin
   *  dato: no se parte la compra»…). Vacío en la recomendada. */
  porque: z.array(Texto(3, 200)).max(6),
});
export type OfertaCompra = z.infer<typeof OfertaCompra>;

export const PartidaCompra = z.strictObject({
  /** Llave estable de la partida (la misma en la lista congelada y al repreciar con la lista vigente). */
  id: z.string().regex(/^[a-z0-9:._ -]{2,80}$/i),
  categoria: CategoriaCompra,
  producto: Texto(2, 200),
  cantidad: z.number().positive().max(100_000),
  unidad: Texto(1, 20),
  origen: OrigenPartida,
  /** Llave del equipo en las listas del mes (`red:<modelo>`, `tabla:<id>`); null = no se compra por lista (paneles, genéricos). */
  item: z.string().max(80).nullable(),
  /** Con lo que se puso el precio (el proveedor del catálogo y el TC del folio). */
  cotizado: z.strictObject({
    proveedor: z.string().max(60).nullable(),
    clave: z.string().max(200).nullable(),
    unitario_usd: Usd.nullable(),
    tc: z.number().positive().nullable(),
    total_mxn: Dinero,
  }),
  /** P1: la meta (mejor proveedor confiable del mes al TC del día de la venta; sin proveedor confiable, lo cotizado). */
  meta: z.strictObject({
    proveedor: z.string().max(60).nullable(),
    componentes: z.array(ComponenteCompra).max(8),
    unitario_usd: Usd.nullable(),
    tc: z.number().positive().nullable(),
    total_mxn: Dinero,
    mes_lista: z.string().regex(/^\d{4}-\d{2}$/).nullable(),
  }),
  alterna: OfertaCompra.nullable(),
  marcas: z.array(MarcaPartida).max(MARCAS_PARTIDA.length),
  notas: z.array(Texto(3, 300)).max(8),
  confirmado: z.boolean(),
  /** P2 (verificación del 30-oct): lo cotizado de esta partida SALIÓ de lo que paga el cliente (un adicional sin costo capturado, o
   *  una batería o un híbrido sin costo en la tabla, tomados de su precio de lista). Si además no hay proveedor confiable (la meta
   *  se queda en lo cotizado), la hoja de Hiram NO enseña su monto: dice «pídele el costo a Diego». */
  costo_es_precio_cliente: z.boolean(),
});
export type PartidaCompra = z.infer<typeof PartidaCompra>;

/** P2: ¿esta partida enseñaría en la hoja de Hiram un monto que es lo que paga el cliente? (meta = lo cotizado = precio al cliente). */
export function montoOcultoAHiram(p: Pick<PartidaCompra, 'costo_es_precio_cliente' | 'meta'>): boolean {
  return p.costo_es_precio_cliente && p.meta.proveedor === null;
}
/** P2: la meta que ve Hiram (sin las partidas cuyo monto es lo que paga el cliente: ni sumado, para que no se saque por resta). */
export function metaParaHiram(l: Pick<ListaCompra, 'partidas'>): { meta_mxn: number; ocultas: number } {
  const ocultas = l.partidas.filter(montoOcultoAHiram);
  return { meta_mxn: Math.round(l.partidas.filter((p) => !montoOcultoAHiram(p)).reduce((s, p) => s + p.meta.total_mxn, 0) * 100) / 100, ocultas: ocultas.length };
}

/** El flete de un proveedor que usa la meta (P3). null = sin dato por escrito. */
export const FleteCompra = z.strictObject({ proveedor: Texto(2, 60), usd: Usd.nullable(), fuente: Texto(3, 300) });
export type FleteCompra = z.infer<typeof FleteCompra>;

/** Diego decidió volver a congelar la meta con la lista del mes y el FIX de ese día (P4: la lista cambió, decide él). */
export const DecisionDiego = z.strictObject({ en: FechaHora, por_nombre: Texto(1, 80) });
export type DecisionDiego = z.infer<typeof DecisionDiego>;
/** De qué venta sale una lista congelada (P4). */
export const BaseMeta = z.strictObject({
  /** El instante de la venta (la entrada a Ganada): el «día de la venta» de P1 y P4. */
  vendida_en: FechaHora,
  /** Quién la marcó Ganada. null = se marcó antes de que el cotizador guardara qué se vendió. */
  vendida_por: Texto(1, 80).nullable(),
  decidio_diego: DecisionDiego.nullable(),
});
export type BaseMeta = z.infer<typeof BaseMeta>;

export const ListaCompra = z.strictObject({
  version: z.literal(1),
  cotizacion_id: Id,
  folio: z.string().regex(/^SX-\d{4}-\d{4,6}$/),
  cliente: Texto(2, 60),
  creada_en: FechaHora,
  creada_por: Texto(1, 80),
  venta: z.strictObject({
    opcion: OpcionId.nullable(),
    nivel: NivelRespaldo.nullable(),
    /** «Exacta: 11 paneles TCL 620 W (6.82 kWp)» o «Confort: 2 Growatt AXE 5.0L + Growatt SPH 10000TL-HU-US». */
    descripcion: Texto(3, 300),
    contado_con_iva: Pesos,
  }),
  tabla_version: z.int().min(1),
  mes_listas: z.string().regex(/^\d{4}-\d{2}$/),
  /** P4 (verificación del 30-oct): la HUELLA del contenido de la lista del mes con que se congeló (sus precios) y cuándo se generó.
   *  Una lista del MISMO mes que se vuelve a generar con otros precios ya no pasa por «la misma»: se compara por contenido. */
  lista_huella: z.string().regex(/^[0-9a-f]{16,64}$/),
  lista_generada_el: Fecha,
  tc: z.strictObject({
    /** El del folio (FIX + colchón), con el que se puso el precio. null = tabla en pesos. */
    folio: z.number().positive().nullable(),
    folio_fecha: Fecha.nullable(),
    /** P1: el FIX con que va la meta, SIN colchón: el que puso Diego en Configuración (la tabla vigente al vender). Su fecha puede
     *  ser ANTERIOR al día de la venta (la hoja lo dice; en rojo si pasa de DIAS_FIX_VIEJO). */
    fix_meta: z.number().positive(),
    fix_meta_fecha: Fecha,
    /** El día de la venta (Hermosillo). */
    dia_venta: Fecha,
  }),
  partidas: z.array(PartidaCompra).min(1).max(200),
  fletes: z.array(FleteCompra).max(10),
  totales: z.strictObject({
    /** Lo que suman las partidas con lo cotizado. */
    cotizado_mxn: Dinero,
    /** El costo directo con el que el motor puso el precio (costosDirectos de la rentabilidad). La aserción: igual al peso. */
    costo_directo_mxn: Dinero,
    /** P1: lo que suman las metas (con los fletes de la meta). */
    meta_mxn: Dinero,
  }),
  /** SOLO Diego (y Carlos en la plataforma): precio de venta, techo y margen. La hoja de Hiram no lo imprime (P2). */
  diego: z.strictObject({
    contado_sin_iva: Pesos,
    comision_e_imprevistos_mxn: Dinero,
    /** El costo directo MÁS ALTO con el que el proyecto sigue en verde / en amarillo. null = ni con costo 0 llega. */
    techo_verde_mxn: Dinero.nullable(),
    techo_amarillo_mxn: Dinero.nullable(),
    margen_cotizado_pct: z.number().finite(),
    semaforo_cotizado: Semaforo,
    margen_con_meta_pct: z.number().finite(),
    semaforo_con_meta: Semaforo,
    avisos: z.array(Texto(3, 400)).max(12),
  }),
  /** Lo que Hiram tiene que ver arriba de la tabla (sin cifras de venta ni de margen). */
  avisos: z.array(Texto(3, 400)).max(20),
  /** P4 (verificación 2 del 30-oct): la VENTA de la que sale esta lista (no cambia al volver a congelarla) y, si la meta ya no va con
   *  la lista y el FIX del día de la venta, cuándo lo decidió Diego. `creada_en`/`creada_por` son de ESTA congelada. Ausente = lista
   *  armada sin esa base (pruebas del motor). */
  base_meta: BaseMeta.optional(),
});
export type ListaCompra = z.infer<typeof ListaCompra>;

// ───────────── La venta (ADENDA z59, C5): qué opción se vendió ─────────────

/** Lo que manda la pantalla al marcar «Ganada»: qué opción se vendió y, en una propuesta de baterías, qué nivel. */
export const CuerpoVenta = z.strictObject({
  opcion: OpcionId.nullable(),
  nivel: NivelRespaldo.nullable(),
  /** P4 (verificación 2 del 30-oct): SOLO Diego (403 a cualquier otro). Al volver a congelar, la meta va con la lista del mes y el FIX
   *  de HOY (no los del día de la venta). Sin esto, volver a congelar rearma las partidas con la cotización de hoy pero conserva la
   *  lista del mes, el FIX y el día de la venta: si la lista cambió más de 3 %, la hoja lo sigue marcando y decide Diego. */
  meta_de_hoy: z.boolean().optional(),
});
export type CuerpoVenta = z.infer<typeof CuerpoVenta>;

/** La venta, SIN costos (la ven todos los que abren la cotización): qué se vendió, cuándo, quién y en cuánto. */
export const VentaCotizacion = z.strictObject({
  /** NO se guarda: lo calcula el servidor al abrirla (verificación del 30-oct). La cotización CAMBIÓ después de la venta (un
   *  adicional, otro precio, otra tabla): la lista congelada ya no es la de lo que se vendió y no se emite la hoja hasta volver a
   *  congelarla. Sin costos (lo ven todos). null o ausente = sigue igual. */
  cambio_despues: z.string().max(800).nullable().optional(),
  opcion: OpcionId.nullable(),
  nivel: NivelRespaldo.nullable(),
  descripcion: Texto(3, 300),
  contado_con_iva: Pesos.nullable(),
  en: FechaHora,
  por: Id,
  por_nombre: Texto(1, 80),
  /** La última versión con PDF al marcarla (0 = ninguna) y si sus precios eran los de la cotización (null = no hay PDF). */
  version_pdf: z.int().nonnegative(),
  pdf_al_dia: z.boolean().nullable(),
  /** P4 (verificación 2 del 30-oct): `en`/`por_nombre` son de la VENTA (no cambian al volver a congelarla); esto es la última vez que
   *  se volvió a congelar. null o ausente = es la congelada del día de la venta. */
  congelada: z.strictObject({ en: FechaHora, por_nombre: Texto(1, 80) }).nullable().optional(),
  /** Se marcó Ganada antes de que el cotizador guardara qué se vendió (se congeló después): `en` es cuando entró a Ganada y
   *  `por_nombre`, quien la congeló. */
  marcada_sin_registro: z.boolean().optional(),
});
export type VentaCotizacion = z.infer<typeof VentaCotizacion>;

/** Lo que se guarda en config «venta.<cotizacion_id>» (NO en la cotización: un servidor de antes la sigue leyendo y guardando igual).
 *  `estado_desde` amarra el registro al momento en que la cotización entró a Ganada: si no coincide con el de la cotización (otra
 *  pestaña la volvió a marcar), el registro no vale y se dice. */
export const VentaGuardada = z.strictObject({
  version: z.literal(1),
  cotizacion_id: Id,
  estado_desde: FechaHora,
  venta: VentaCotizacion,
  /** La lista CONGELADA (P4). null = no se armó (ver `falla` o `no_aplica`). */
  lista: ListaCompra.nullable(),
  /** La aserción no cuadró (o faltó algo): no se emite la hoja. Lo ve solo Diego. */
  falla: z.string().max(2_000).nullable(),
  /** Tipos que todavía no llevan lista (ampliación de baterías, recorte de punta, sin opciones). */
  no_aplica: z.string().max(400).nullable(),
  /** P4 (verificación del 30-oct): las ventas que esta REEMPLAZÓ (se volvió a congelar, o se pasó a otro estado y se volvió a marcar
   *  Ganada), de la más nueva a la más vieja. Diego lo ve y la hoja de Hiram dice que la anterior ya no sirve. */
  anteriores: z.array(z.strictObject({ en: FechaHora, por_nombre: Texto(1, 80), descripcion: Texto(3, 300), meta_mxn: Dinero.nullable() })).max(10),
  /** P4 (verificación 2 del 30-oct): la BASE de la meta, la de la venta. Volver a congelar (o pasar por otro estado y volver a Ganada)
   *  la HEREDA: la lista del mes (por su huella) y el FIX con que se congeló la primera vez. Solo Diego la cambia (`meta_de_hoy`).
   *  Ausente = registro de antes de esta regla (se toma de su lista). */
  origen: z
    .strictObject({
      vendida_en: FechaHora,
      vendida_por: Texto(1, 80).nullable(),
      /** null = no se pudo fijar (la tabla vigente no traía el FIX). */
      meta: z.strictObject({ mes_listas: z.string().regex(/^\d{4}-\d{2}$/), lista_huella: z.string().regex(/^[0-9a-f]{16,64}$/), lista_generada_el: Fecha, fix: z.number().positive(), fix_fecha: Fecha }).nullable(),
      decidio_diego: DecisionDiego.nullable(),
    })
    .optional(),
});
export type VentaGuardada = z.infer<typeof VentaGuardada>;
export type OrigenVenta = NonNullable<VentaGuardada['origen']>;

const NOMBRE_OPCION_VENTA: Readonly<Record<string, string>> = { arranque: 'Arranque', exacta: 'Exacta', holgada: 'Holgada' };
const NOMBRE_NIVEL_VENTA: Readonly<Record<string, string>> = { esencial: 'Esencial', confort: 'Confort', completa: 'Casa completa' };
const pesosVenta = (x: number) => `$${Math.round(x).toLocaleString('en-US')}`;

/** Lo que el vendedor puede elegir como «lo que se vendió». */
export interface OpcionDeVenta {
  opcion: z.infer<typeof OpcionId> | null;
  nivel: z.infer<typeof NivelRespaldo> | null;
  /** «Exacta · 11 paneles (6.82 kWp) · $123,100 de contado». */
  texto: string;
  contado: number | null;
}

/** ADENDA z59 (C5): ¿qué hay que elegir al marcar esta cotización Ganada? Propuesta de baterías (formato baterías con niveles con
 *  precio) → el NIVEL; solar → la OPCIÓN; sin opciones (ampliación de baterías, recorte de punta, sin precios) → nada, con el porqué
 *  (esa venta no lleva lista de compra todavía). La usan el servidor (422 si falta) y la pantalla (qué selector enseña). Solo lee
 *  precios (no costos): sirve igual con la vista de un vendedor. */
export function opcionesDeVenta(b: { cuestionario: Pick<Cuestionario, 'tipo' | 'tarifa'> & { recorte_punta?: Cuestionario['recorte_punta'] }; resultado: ResultadoMotor | null }): { pide: 'opcion' | 'nivel'; opciones: OpcionDeVenta[] } | { pide: null; motivo: string } {
  if (b.cuestionario.tipo === 'ampliacion_baterias') return { pide: null, motivo: 'La ampliación de baterías lleva su propio costeo: su lista de compra todavía no se arma en el cotizador.' };
  const r = b.resultado;
  if (!r) return { pide: null, motivo: 'La cotización no tiene precios calculados: no hay de qué armar la lista de compra.' };
  const formato = formatoPara(b.cuestionario, r.tarifa);
  if (formato === 'horaria') return { pide: null, motivo: 'El recorte de punta lleva su propio costeo: su lista de compra todavía no se arma en el cotizador.' };
  if (formato === 'baterias' && r.respaldo && r.respaldo.niveles.some((n) => n.precio)) {
    const opciones = r.respaldo.niveles
      .filter((n) => n.precio)
      .map((n) => {
        const o = n.sistema ? r.opciones.find((x) => x.id === n.sistema!.opcion) : undefined;
        const bat = `${n.bateria.modulos} × ${n.bateria.marca} ${n.bateria.modelo}`;
        return { opcion: n.opcion ?? null, nivel: n.id, texto: `${NOMBRE_NIVEL_VENTA[n.id]} · ${bat}${o ? ` con ${o.paneles} paneles (${o.kwp.toFixed(2)} kWp)` : ''} · ${pesosVenta(n.precio!.contado)} de contado`, contado: n.precio!.contado };
      });
    return { pide: 'nivel', opciones };
  }
  if (!r.opciones.length) return { pide: null, motivo: 'La cotización no tiene opciones con precio.' };
  return {
    pide: 'opcion',
    opciones: r.opciones.map((o) => ({ opcion: o.id, nivel: null, texto: `${o.mejor_retorno ? 'Mejor retorno' : NOMBRE_OPCION_VENTA[o.id]} · ${o.paneles} paneles (${o.kwp.toFixed(2)} kWp) · ${pesosVenta(o.precio.contado)} de contado`, contado: o.precio.contado })),
  };
}

/** Clave de config de la venta de una cotización. */
export function claveVenta(cotizacionId: string): string {
  return `venta.${cotizacionId}`;
}

/** Lo que ve DIEGO en la cotización (detalle): el resumen de la lista congelada. vista.ts lo quita a todos los demás. */
export const ResumenListaCompra = z.strictObject({
  /** «cambio»: la cotización cambió después de la venta (la hoja no se emite hasta volver a congelarla). */
  estado: z.enum(['lista', 'falla', 'no_aplica', 'sin_registro', 'cambio']),
  motivo: z.string().max(2_000).nullable(),
  partidas: z.int().nonnegative(),
  mes_listas: z.string().max(7).nullable(),
  meta_mxn: Dinero.nullable(),
  cotizado_mxn: Dinero.nullable(),
  techo_verde_mxn: Dinero.nullable(),
  sin_proveedor_confiable: z.int().nonnegative(),
  avisos: z.array(z.string().max(400)).max(20),
  /** «Reemplaza a la del 29 de octubre (Exacta…, meta $131,848) que congeló Miguel.» null = es la primera. Ausente = servidor de antes. */
  reemplaza: z.string().max(600).nullable().optional(),
  /** En ROJO: el FIX de la meta es de varios días antes de la venta, la lista es de hace más de 30 días (alertasDeLista). */
  alertas: z.array(z.string().max(600)).max(6).optional(),
});
export type ResumenListaCompra = z.infer<typeof ResumenListaCompra>;

/** «Reemplaza a la del 29-oct (Exacta: 23 paneles…, meta $131,848) que congeló Miguel Prueba.» (sin la meta para quien no es Diego). */
export function textoReemplaza(g: Pick<VentaGuardada, 'anteriores'>, conMeta: boolean): string | null {
  const a = g.anteriores[0];
  if (!a) return null;
  const dia = diaEnPalabras(a.en);
  const mas = g.anteriores.length > 1 ? ` (y ${g.anteriores.length - 1} antes)` : '';
  return `Reemplaza a la lista del ${dia} (${a.descripcion}${conMeta && a.meta_mxn !== null ? `, meta ${pesosVenta(a.meta_mxn)}` : ''}) que congeló ${a.por_nombre}${mas}: no compres con la anterior.`.slice(0, 600);
}

/** El resumen de lo guardado (o de que no hay registro válido). `cambio` = la cotización cambió después de la venta (o ya tiene opciones
 *  y no se dijo cuál se vendió); `p4` = la lista del mes vigente cambió más de 3 % contra la congelada (decide Diego): va en ROJO con
 *  las alertas (verificación 2 del 30-oct: antes solo se veía dentro de la hoja). */
export function resumenListaCompra(g: VentaGuardada | null, cambio: { diego: string } | null = null, p4: string | null = null): ResumenListaCompra {
  if (!g) return { estado: 'sin_registro', motivo: 'Esta cotización está en Ganada sin registro de qué opción se vendió (se marcó antes del 30-oct o desde otra pestaña). Para armar su lista de compra, congélala con «Volver a congelar» eligiendo la opción.', partidas: 0, mes_listas: null, meta_mxn: null, cotizado_mxn: null, techo_verde_mxn: null, sin_proveedor_confiable: 0, avisos: [], reemplaza: null };
  const reemplaza = textoReemplaza(g, true);
  const vacio = { partidas: 0, mes_listas: null, meta_mxn: null, cotizado_mxn: null, techo_verde_mxn: null, sin_proveedor_confiable: 0, avisos: [], reemplaza };
  if (g.lista) {
    const l = g.lista;
    const base = { partidas: l.partidas.length, mes_listas: l.mes_listas, meta_mxn: l.totales.meta_mxn, cotizado_mxn: l.totales.cotizado_mxn, techo_verde_mxn: l.diego.techo_verde_mxn, sin_proveedor_confiable: l.partidas.filter((p) => p.marcas.includes('sin_proveedor_confiable')).length, avisos: [...l.avisos, ...l.diego.avisos].slice(0, 20), reemplaza, alertas: [...(p4 ? [p4.slice(0, 600)] : []), ...alertasDeLista(l)].slice(0, 6) };
    // Fallo RUIDOSO (verificación del 30-oct): la cotización cambió después de la venta → la lista congelada ya no es la de lo vendido.
    if (cambio) return { estado: 'cambio', motivo: cambio.diego.slice(0, 2_000), ...base };
    return { estado: 'lista', motivo: null, ...base };
  }
  if (g.falla) return { estado: 'falla', motivo: g.falla, ...vacio };
  // Verificación 2 del 30-oct: se marcó Ganada sin opciones (sin precios) y DESPUÉS se calculó: ya no «no lleva lista»; falta decir
  // qué se vendió (en rojo, no en gris con un motivo que ya es falso).
  if (cambio) return { estado: 'cambio', motivo: cambio.diego.slice(0, 2_000), ...vacio };
  return { estado: 'no_aplica', motivo: g.no_aplica ?? 'Esta cotización no lleva lista de compra.', ...vacio };
}

const r2 = (x: number) => Math.round(x * 100) / 100;
const diasEntre = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

/** ALERTAS en ROJO de una lista congelada (la hoja de Hiram, la de Diego y su pantalla): el FIX de la meta es de varios días antes de
 *  la venta (P1 pide el del día de la venta: verificación del 30-oct, la hoja decía «al FIX del día» con un FIX de 20 días), y la lista
 *  de proveedores es de hace más de 30 días. Sin cifras de venta ni de margen (las ve Hiram). */
export function alertasDeLista(l: Pick<ListaCompra, 'tc' | 'mes_listas' | 'partidas'> & { base_meta?: ListaCompra['base_meta'] }): string[] {
  const out: string[] = [];
  // Contra qué día: el de la venta (P1, P4) o, si Diego decidió volver a congelar la meta con la lista y el FIX de ese día, ese día.
  const dd = l.base_meta?.decidio_diego ?? null;
  const ref = dd ? fechaHermosillo(new Date(dd.en)) : l.tc.dia_venta;
  const cuando = dd ? `del día en que Diego volvió a congelar la meta (${diaEnPalabras(ref)})` : `de la venta (${diaEnPalabras(ref)})`;
  const dFix = diasEntre(l.tc.fix_meta_fecha, ref);
  // En los dos sentidos (verificación 2 del 30-oct): un FIX de DESPUÉS de la venta tampoco es el del día de la venta.
  if (Math.abs(dFix) > DIAS_FIX_VIEJO)
    out.push(`El FIX de la meta (${l.tc.fix_meta.toFixed(4)}, del ${diaEnPalabras(l.tc.fix_meta_fecha)}) es de ${Math.abs(dFix)} días ${dFix > 0 ? 'antes' : 'después'} ${cuando}, no del día ${dd ? 'en que se decidió' : 'de la venta'}: si el dólar se movió, la meta en pesos también. Compara en dólares con la cotización del proveedor.`);
  const dLista = diasEntre(`${l.mes_listas}-01`, ref);
  if (dLista > DIAS_LISTA_VIEJA || l.partidas.some((p) => p.marcas.includes('lista_vieja')))
    out.push(`Las listas de proveedores son de ${mesEnPalabras(l.mes_listas)} y ${dd ? `Diego volvió a congelar la meta el ${diaEnPalabras(ref)}` : `la venta es del ${diaEnPalabras(ref)}`}: tienen más de ${DIAS_LISTA_VIEJA} días. Confirma el precio con cada proveedor antes de comprar.`);
  return out;
}

/** ASERCIÓN (fallo ruidoso, ADENDA z59): una lista que no cuadra NO se emite. Revisa (1) que lo que suman las partidas con lo
 *  cotizado sea, al peso, el costo directo con el que se puso el precio; (2) que los totales sean la suma de sus partidas; (3) que
 *  ninguna partida sea negativa. Truena con el detalle (solo lo ve Diego). La corre el motor al armarla y el servidor al servirla. */
export function exigirListaCuadra(l: Pick<ListaCompra, 'partidas' | 'totales' | 'folio'>): void {
  const malas = l.partidas.filter((p) => !(p.cotizado.total_mxn >= 0) || !(p.meta.total_mxn >= 0));
  if (malas.length) throw new Error(`Lista de compra de ${l.folio}: partidas con monto negativo o vacío (${malas.map((p) => p.producto).join('; ')}).`);
  const cot = r2(l.partidas.reduce((s, p) => s + p.cotizado.total_mxn, 0));
  const meta = r2(l.partidas.reduce((s, p) => s + p.meta.total_mxn, 0));
  if (Math.abs(cot - l.totales.cotizado_mxn) > 0.02 || Math.abs(meta - l.totales.meta_mxn) > 0.02)
    throw new Error(`Lista de compra de ${l.folio}: los totales no son la suma de sus partidas (cotizado ${cot} contra ${l.totales.cotizado_mxn}; meta ${meta} contra ${l.totales.meta_mxn}).`);
  if (Math.abs(cot - l.totales.costo_directo_mxn) > TOLERANCIA_CUADRE_MXN)
    throw new Error(
      `Lista de compra de ${l.folio}: lo que suman las partidas ($${cot.toFixed(2)}) no es el costo directo con el que se puso el precio ($${l.totales.costo_directo_mxn.toFixed(2)}; diferencia $${r2(cot - l.totales.costo_directo_mxn).toFixed(2)}). No se emite la hoja: alguna partida falta, sobra o tiene otro costo que el del cálculo.`,
    );
}

// ───────────── Sobre de datos para el CRM de Carlos (bloque «costeo» del sobre v2; COSTEO-PARA-HIRAM §3.1 C7) ─────────────

/** Tope del sobre que acepta la puerta de la plataforma (16 KB). */
export const TOPE_SOBRE_CRM_BYTES = 16_384;

export const PartidaCosteoCrm = z.strictObject({
  categoria: CategoriaCompra,
  producto: Texto(2, 200),
  clave: z.string().max(200).nullable(),
  cantidad: z.number().positive(),
  unidad: Texto(1, 20),
  /** Unitarios SIN IVA en la moneda de `moneda` (USD cuando el proveedor cotiza en dólares, para que Hiram compare igual). */
  unitario_meta: z.number().nonnegative(),
  unitario_estimado: z.number().nonnegative(),
  moneda: z.enum(['USD', 'MXN']),
  tc: z.number().positive().nullable(),
  /** «Solar Center · PV-IN-TL5KX2-GW · lista 2026-10 · alterna DMSolar» o «SIN CONFIRMAR»: va a supplier_preference. */
  proveedor: Texto(2, 300),
  alterna: z.string().max(120).nullable(),
  origen: OrigenPartida,
  mes_lista: z.string().max(7).nullable(),
  confirmado: z.boolean(),
  /** P2: el monto de esta partida es lo que paga el cliente (sin costo capturado): la plataforma NO se lo enseña a Hiram ni a Nubia. */
  monto_es_precio_cliente: z.boolean(),
});
export const CosteoCrm = z.strictObject({
  mes_listas: z.string().regex(/^\d{4}-\d{2}$/),
  tabla_version: z.int().min(1),
  /** fix_meta = el FIX con que va la meta (el de Configuración, sin colchón) y su fecha; dia_venta = el día de la venta. */
  tc: z.strictObject({ folio: z.number().positive().nullable(), folio_fecha: Fecha.nullable(), fix_meta: z.number().positive(), fix_meta_fecha: Fecha, dia_venta: Fecha }),
  incluye_iva: z.literal(false),
  genera_iva: z.literal(true),
  costo_estimado_sin_iva: z.number().nonnegative(),
  meta_compra_sin_iva: z.number().nonnegative(),
  /** Solo los lee quien tiene view_margins en la plataforma (Diego y Carlos). */
  techo_verde_sin_iva: z.number().nullable(),
  techo_amarillo_sin_iva: z.number().nullable(),
  contado_sin_iva: z.number().nonnegative(),
  partidas: z.array(PartidaCosteoCrm).min(1).max(200),
});
export type CosteoCrm = z.infer<typeof CosteoCrm>;

/** El bloque «costeo» del sobre v2 con la lista congelada (P1–P4). Se valida antes de devolverlo (la misma regla que pide
 *  fn_costeo_a_presupuesto: categoría válida, cantidad > 0, costo ≥ 0) y truena si pesa más que el tope de la puerta. */
export function costeoParaCrm(l: ListaCompra): CosteoCrm {
  exigirListaCuadra(l);
  const unitario = (total: number, p: PartidaCompra, usd: number | null) => (usd !== null ? usd : r2(total / p.cantidad));
  const c: CosteoCrm = {
    mes_listas: l.mes_listas,
    tabla_version: l.tabla_version,
    tc: { ...l.tc },
    incluye_iva: false,
    genera_iva: true,
    costo_estimado_sin_iva: l.totales.cotizado_mxn,
    meta_compra_sin_iva: l.totales.meta_mxn,
    techo_verde_sin_iva: l.diego.techo_verde_mxn,
    techo_amarillo_sin_iva: l.diego.techo_amarillo_mxn,
    contado_sin_iva: l.diego.contado_sin_iva,
    partidas: l.partidas.map((p) => {
      const usd = p.meta.unitario_usd !== null && p.cotizado.unitario_usd !== null;
      const clave = p.meta.componentes.length ? p.meta.componentes.map((x) => x.clave).join(' + ') : p.cotizado.clave;
      const proveedor = p.meta.proveedor
        ? `${p.meta.proveedor}${clave ? ` · ${clave}` : ''}${p.meta.mes_lista ? ` · lista ${p.meta.mes_lista}` : ''}${p.alterna ? ` · alterna ${p.alterna.proveedor}` : ''}`
        : p.marcas.includes('sin_proveedor_confiable')
          ? `SIN CONFIRMAR · sin proveedor confiable${p.alterna ? ` · alterna ${p.alterna.proveedor}` : ''}`
          : p.marcas.includes('precio_diego')
            ? 'Precio de Diego'
            : 'SIN CONFIRMAR';
      return {
        categoria: p.categoria,
        producto: p.producto,
        clave: clave ?? null,
        cantidad: p.cantidad,
        unidad: p.unidad,
        unitario_meta: usd ? p.meta.unitario_usd! : unitario(p.meta.total_mxn, p, null),
        unitario_estimado: usd ? p.cotizado.unitario_usd! : unitario(p.cotizado.total_mxn, p, null),
        moneda: usd ? 'USD' : 'MXN',
        tc: usd ? p.meta.tc : null,
        proveedor: proveedor.slice(0, 300),
        alterna: p.alterna ? p.alterna.proveedor : null,
        origen: p.origen,
        mes_lista: p.meta.mes_lista,
        confirmado: p.confirmado,
        monto_es_precio_cliente: montoOcultoAHiram(p),
      };
    }),
  };
  const ok = CosteoCrm.parse(c);
  const bytes = new TextEncoder().encode(JSON.stringify(ok)).length;
  if (bytes > TOPE_SOBRE_CRM_BYTES) throw new Error(`El costeo de ${l.folio} pesa ${bytes} bytes: la puerta del CRM acepta hasta ${TOPE_SOBRE_CRM_BYTES}.`);
  return ok;
}
